import type { ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';

import { cn } from '@/shared/lib/cn';

import { Alert } from './Alert';
import { Button } from './Button';
import { Card, CardBody, CardHeader } from './Card';
import { PageHeader } from './PageHeader';
import { Skeleton, SkeletonText } from './Skeleton';

export interface DetailSectionItem {
  key: string;
  /** Titulo no menu lateral, com o texto do OnPoint. */
  title: string;
  /** O conteudo da secao, montado so quando ela esta aberta. */
  render: () => ReactNode;
}

export interface DetailLayoutProps {
  title: string;
  description?: string | undefined;
  /** Etiquetas abaixo do titulo: situacao, bloqueio, limite. */
  meta?: ReactNode | undefined;
  /** Acoes do cabecalho, como "Voltar para a lista". */
  actions?: ReactNode | undefined;
  /** Aviso entre o cabecalho e as secoes, como o motivo do bloqueio. */
  banner?: ReactNode | undefined;
  /**
   * As secoes do menu lateral, na ordem do OnPoint, ja filtradas pela permissao de
   * quem ve: a tela esconde o que nao abre, e o servidor decide o acesso.
   */
  sections: DetailSectionItem[];
  /** Nome do menu para o leitor de tela, como "Secoes do tomador". */
  navLabel: string;
  /**
   * Parametro do endereco com a secao aberta. A secao fica no endereco para que o
   * link copiado e o voltar do navegador abram o mesmo lugar.
   */
  param?: string | undefined;
}

/**
 * Detalhe com cabecalho, menu lateral e secoes (P-FUN-2b), extraido do detalhe do
 * tomador para o corretor, o segurado, a apolice e a cotacao, que no OnPoint tem a
 * mesma forma: um cadastro aberto, com as secoes a esquerda.
 *
 * Cada secao le a propria rota. O layout so escolhe qual esta aberta: a pedida no
 * endereco, se estiver entre as visiveis, ou a primeira.
 */
export function DetailLayout({
  title,
  description,
  meta,
  actions,
  banner,
  sections,
  navLabel,
  param = 'secao',
}: DetailLayoutProps) {
  const [parametros, setParametros] = useSearchParams();
  const pedida = parametros.get(param);
  const ativa = sections.find((secao) => secao.key === pedida) ?? sections[0];

  function abrir(chave: string) {
    const proximos = new URLSearchParams(parametros);
    proximos.set(param, chave);
    setParametros(proximos, { replace: true });
  }

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title={title}
        description={description}
        meta={meta}
        actions={actions}
      />

      {banner}

      <div className="grid gap-5 lg:grid-cols-[14rem_1fr]">
        {/* O menu e um cartao, e a secao aberta ganha o fundo e a faixa da marca. */}
        <nav
          aria-label={navLabel}
          className="surface-card flex flex-col gap-0.5 self-start p-1.5"
        >
          {sections.map((secao) => (
            <button
              key={secao.key}
              type="button"
              aria-current={secao.key === ativa?.key ? 'page' : undefined}
              onClick={() => abrir(secao.key)}
              className={cn(
                'rounded-lg px-3 py-2 text-left text-sm',
                'transition-[background-color,color,box-shadow] duration-(--duration-fast)',
                secao.key === ativa?.key
                  ? 'bg-brand-50 font-semibold text-brand-700 shadow-[inset_3px_0_0_var(--color-brand-500)]'
                  : 'font-medium text-ink-600 hover:bg-ink-100 hover:text-ink-900',
              )}
            >
              {secao.title}
            </button>
          ))}
        </nav>
        <div className="min-w-0">{ativa ? ativa.render() : null}</div>
      </div>
    </div>
  );
}

/** O detalhe enquanto a entidade carrega. */
export function DetailLoading() {
  return (
    <div className="flex flex-col gap-5" aria-busy="true">
      <Skeleton className="h-8 w-64" />
      <Card>
        <CardBody>
          <SkeletonText lines={5} />
        </CardBody>
      </Card>
    </div>
  );
}

/** O detalhe que nao abriu, com o caminho de volta. */
export function DetailError({
  title,
  alertTitle,
  message,
  backLabel = 'Voltar para a lista',
  onBack,
}: {
  title: string;
  alertTitle: string;
  message: string;
  backLabel?: string | undefined;
  onBack: () => void;
}) {
  return (
    <div className="flex flex-col gap-5">
      <PageHeader title={title} />
      <Alert tone="danger" title={alertTitle}>
        <div className="flex flex-col gap-3">
          <p>{message}</p>
          <Button size="sm" variant="secondary" onClick={onBack}>
            {backLabel}
          </Button>
        </div>
      </Alert>
    </div>
  );
}

/** Cartao de uma secao do detalhe, com os estados de carregamento e de erro. */
export function DetailCard({
  title,
  description,
  action,
  loading,
  error,
  children,
}: {
  title: string;
  description?: string | undefined;
  action?: ReactNode;
  loading?: boolean | undefined;
  error?: Error | null | undefined;
  children: ReactNode;
}) {
  return (
    <Card>
      <CardHeader
        title={title}
        {...(description ? { description } : {})}
        action={action}
      />
      <CardBody className="flex flex-col gap-5">
        {error ? (
          <Alert tone="danger" title="Nao foi possivel carregar">
            {error.message}
          </Alert>
        ) : loading ? (
          <SkeletonText lines={4} />
        ) : (
          children
        )}
      </CardBody>
    </Card>
  );
}

/** Abas internas de uma secao, como as de "Cadastro Completo". */
export function DetailTabs<T extends string>({
  tabs,
  active,
  onChange,
  label,
}: {
  tabs: { key: T; title: string }[];
  active: T;
  onChange: (key: T) => void;
  label: string;
}) {
  return (
    // As abas sao um seletor segmentado: a aberta e um cartao branco com a cor da
    // empresa sobre o trilho cinza, e as outras ficam no texto do trilho.
    <div
      role="tablist"
      aria-label={label}
      className="flex w-fit max-w-full flex-wrap gap-1 rounded-xl bg-ink-100 p-1 ring-1 ring-line ring-inset"
    >
      {tabs.map((aba) => (
        <button
          key={aba.key}
          type="button"
          role="tab"
          aria-selected={aba.key === active}
          onClick={() => onChange(aba.key)}
          className={cn(
            'rounded-lg px-3 py-1.5 text-xs font-semibold',
            'transition-[background-color,color,box-shadow] duration-(--duration-fast)',
            'focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-brand-500',
            aba.key === active
              ? 'bg-surface text-brand-700 shadow-(--shadow-sm) ring-1 ring-line'
              : 'text-ink-600 hover:bg-surface/60 hover:text-ink-900',
          )}
        >
          {aba.title}
        </button>
      ))}
    </div>
  );
}
